import { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Page } from 'seagull';
import { uniqBy, keyBy } from 'lodash-es';
import { useIoContext, useStoreActions, useStoreStatic } from "../../contexts";
import { routes } from "../../constants";
import ImportWizard from "../../components/ImportWizard/ImportWizard";
import { useServiceTemplateApi } from "../../hooks/useServiceTemplateApi";
import { selectors } from "../../store/state-selectors";
import { parseAndValidateAgreementCsv } from "./parseAndValidateAgreementCsv";
import { serviceModel, serviceTemplateModel } from "../../models";
import { ErrorResponse } from "../../infra/ErrorResponse";
import { useContacts } from "../../hooks/reactQuery/queries/useContacts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useAppConfig } from "../../store/store-context";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { jsx as _jsx } from "react/jsx-runtime";
const title = 'Import agreements';
const servicesEntity = 'service templates';
const contactsEntity = 'contacts';
const oldCsvTemplateUrl = 'https://storage.googleapis.com/anchor-statics/Anchor_Bulk_Import-2024-11-11.zip';
const csvTemplateUrl = 'https://storage.googleapis.com/anchor-statics/Anchor_Bulk_Import-2025-01-11.zip';
const getServiceUniqueKey = s => s.name;
function applyExistingServiceTemplateId(draft, existingServiceTemplates) {
  var _draft$serviceBundles;
  const applyExistingServiceTemplateId = s => {
    const existingServiceTemplate = existingServiceTemplates[getServiceUniqueKey(s)];
    if (existingServiceTemplate) {
      s.serviceTemplateId = existingServiceTemplate.id;
    }
  };
  draft.services.forEach(applyExistingServiceTemplateId);
  (_draft$serviceBundles = draft.serviceBundles) === null || _draft$serviceBundles === void 0 || _draft$serviceBundles.bundles.forEach(_ref => {
    let {
      services
    } = _ref;
    return services.forEach(applyExistingServiceTemplateId);
  });
}

/** Imports agreements from a CSV file and saves them as drafts. */
export default function ImportAgreements() {
  const history = useHistory();
  const {
    logBI,
    api
  } = useStoreActions();
  const {
    getState
  } = useStoreStatic();
  const {
    getNow
  } = useIoContext();
  const serviceTemplateApi = useServiceTemplateApi();
  const [currentSaveData, setCurrentSaveData] = useState();
  const {
    PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD
  } = useFeatureFlags();
  const {
    generateUniqueId
  } = useAppConfig();
  const {
    contacts,
    refresh
  } = useContacts();
  const keyedServiceTemplatesRef = useRef({});
  useSetDocumentTitle(title);
  const onStartSave = async items => {
    const drafts = items.map(i => i.draft);
    const newContacts = items.map(i => i.contact);
    const [existingServiceTemplates] = await Promise.all([serviceTemplateApi.getActive(), refresh]);
    const initialKeyedServiceTemplates = keyBy(existingServiceTemplates, getServiceUniqueKey);
    keyedServiceTemplatesRef.current = initialKeyedServiceTemplates;
    const newServices = [];
    drafts.forEach(d => {
      var _d$serviceBundles;
      d.services.forEach(s => {
        const service = serviceModel.fromDTO(s);
        if (service != null) newServices.push(service);
      });
      (_d$serviceBundles = d.serviceBundles) === null || _d$serviceBundles === void 0 || _d$serviceBundles.bundles.forEach(_ref2 => {
        let {
          services
        } = _ref2;
        return services.forEach(s => {
          const service = serviceModel.fromDTO(s);
          if (service != null) newServices.push(service);
        });
      });
    });
    const uniqueNewServices = uniqBy(newServices, getServiceUniqueKey);
    const uniqueNewContacts = uniqBy(newContacts, c => c.uniqueKey());

    // Create new service-templates if needed.
    const existingServiceTemplatesUniqueKeys = new Set(existingServiceTemplates.map(getServiceUniqueKey));
    const servicesToSave = uniqueNewServices.filter(s => {
      return !existingServiceTemplatesUniqueKeys.has(getServiceUniqueKey(s));
    });
    let savedServices = 0;
    const setServicesSaveData = () => setCurrentSaveData({
      entity: servicesEntity,
      saved: savedServices,
      total: servicesToSave.length
    });
    setServicesSaveData();
    for (const s of servicesToSave) {
      const template = serviceTemplateModel.fromService(s);
      await serviceTemplateApi.create(serviceTemplateModel.makeServiceTemplate({
        id: template.id,
        name: template.name,
        description: template.description,
        cost: template.cost,
        billing: template.billing
      }));
      savedServices++;
      setServicesSaveData();
    }

    // Refresh service-templates to attach them later to the saved drafts.
    if (servicesToSave.length > 0) {
      const newServiceTemplates = await serviceTemplateApi.getActive();
      const newKeyedServiceTemplates = keyBy(newServiceTemplates, getServiceUniqueKey);
      keyedServiceTemplatesRef.current = newKeyedServiceTemplates;
    }

    // Create new contacts if needed.
    const existingContactsUniqueKeys = new Set(contacts.map(c => c.uniqueKey()));
    const contactsToSave = uniqueNewContacts.filter(c => !existingContactsUniqueKeys.has(c.uniqueKey()));
    let savedContacts = 0;
    const setContactsSaveData = errorMsg => setCurrentSaveData({
      entity: contactsEntity,
      saved: savedContacts,
      total: contactsToSave.length,
      errorMsg
    });
    setContactsSaveData();
    for (const c of contactsToSave) {
      await api.contact.create(c).catch(err => {
        const msg = new ErrorResponse(err).saveContactErrorText();
        setContactsSaveData(msg);
        throw err;
      });
      savedContacts++;
      setContactsSaveData();
    }

    // Refresh contacts to attach them later to the saved drafts.
    if (contactsToSave.length) {
      await refresh();
    }
    setCurrentSaveData(undefined);
  };
  return /*#__PURE__*/_jsx(Page, {
    skin: "secondary",
    header: title,
    children: /*#__PURE__*/_jsx(ImportWizard, {
      entityName: "agreements",
      saveEntityName: "drafts",
      currentSaveData: currentSaveData,
      importBtnText: "Import as drafts",
      csvTemplateUrl: PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD ? csvTemplateUrl : oldCsvTemplateUrl,
      testid: "import-agreements",
      onStartSave: onStartSave,
      saveItem: _ref3 => {
        let {
          draft,
          contact: newContact
        } = _ref3;
        const contactUniqueKeyFromDraft = newContact.uniqueKey();
        const contact = contacts.find(c => c.uniqueKey() === contactUniqueKeyFromDraft);
        draft.clientContactId = contact ? contact.id() : null;
        applyExistingServiceTemplateId(draft, keyedServiceTemplatesRef.current);
        return api.draft.save(draft);
      },
      parseAndValidateCsv: async data => {
        var _selectors$user$busin;
        logBI(10901);
        const libraryLegalTermsId = (_selectors$user$busin = selectors.user.businessSettings(getState())) === null || _selectors$user$busin === void 0 ? void 0 : _selectors$user$busin.legalTermsDocumentId;
        const legalTerms = libraryLegalTermsId ? {
          type: 'libraryLegalTermsId',
          libraryLegalTermsId
        } : null;
        const availableLegalTerms = await api.business.getLegalTerms();
        const {
          items,
          errors
        } = await parseAndValidateAgreementCsv({
          data,
          defaultDraftsValues: {
            legalTerms
          },
          availableLegalTerms,
          now: getNow(),
          PROPOSAL_EXPIRATION_DATE_BULK_UPLOAD,
          generateUniqueId
        });
        logBI(10902, {
          numAgreements: items.length,
          numErrors: errors.length
        });
        return {
          items,
          errors
        };
      },
      onEvent: name => {
        if (name === 'csvTemplateDownload') {
          logBI(10900);
        } else if (name === 'startImportItems') {
          logBI(10903);
        } else if (name === 'redirectToItems') {
          history.push(routes.draftsDashboard);
        } else if (name === 'afterSaveFailure') {
          logBI(10908);
        } else if (name === 'afterSaveSuccess') {
          logBI(10909);
        }
      }
    })
  });
}